// Modify: src/context/PreferencesContext.js
import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { getMyPreferences } from '../services/preferencesApi';

const PreferencesContext = createContext(null);

/**
 * Provides application-wide user preferences data.
 * Fetches the data once on initial load and makes it available to all child components.
 */
export const PreferencesProvider = ({ children }) => {
  const [preferences, setPreferences] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPreferences = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const prefsData = await getMyPreferences();
        setPreferences(prefsData);
      } catch (err) {
        setError(err);
        console.error("Failed to load user preferences:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPreferences();
  }, []);

  /**
   * Updates the preferences state in the context.
   * This should be called after a successful API update to keep the app's state in sync.
   * @param {object} newPreferences The full, updated preferences object.
   */
  const updatePreferences = useCallback((newPreferences) => {
    setPreferences(newPreferences);
  }, []);

  const value = useMemo(() => ({
    preferences,
    isLoading,
    error,
    updatePreferences,
  }), [preferences, isLoading, error, updatePreferences]);

  return (
    <PreferencesContext.Provider value={value}>
      {children}
    </PreferencesContext.Provider>
  );
};

/**
 * Custom hook to access the user preferences context.
 * @returns {{
 *   preferences: object | null,
 *   isLoading: boolean,
 *   error: Error | null,
 *   updatePreferences: (newPreferences: object) => void
 * }}
 */
export const usePreferences = () => {
  const context = useContext(PreferencesContext);
  if (context === null) {
    throw new Error('usePreferences must be used within a PreferencesProvider');
  }
  return context;
};